/**
 * LOS DANOS UNO POR UNO, CON SU COMPENSACION (INV-CRUCES-PRESENTACION-T1; decision de Josse D-138, SI de Ares #30120).
 *
 * D-138 ampliacion 3 (Ares #31237, Josse 20-oct): el usuario NO ve la palabra cruce ni sus registros. Un dano compensado
 * del todo no se lista; uno compensado en parte muestra solo lo vigente; nada se tacha ni se etiqueta. Cuenta en la fecha
 * del original (antes: tachado con la cifra vigente al lado).
 * La regla vive en el servidor (nexo_private.v_danos_vigentes, nexo-api 566): el panel LEE estado, valores y totales y no
 * recalcula nada. totalVigente es la cifra del indicador; totalOriginal es el bruto (el que vio Josse en T150).
 * Sin DOM, para el check.
 *
 * @module application/indicadores/danosVigentes
 */

const de = (o, ...ks) => { for (const k of ks) if (o && o[k] !== undefined && o[k] !== null) return o[k]; return undefined; };
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const ESTADOS = new Set(['vigente', 'compensado_parcial', 'compensado']);

/** La respuesta de la API en una forma (camel y snake). Ordenada por fecha del original. */
export function normalizarDanosVigentes(data) {
  const d = data || {};
  const items = (Array.isArray(de(d, 'items', 'Items')) ? de(d, 'items', 'Items') : []).map((x) => {
    const estado = String(de(x, 'estado', 'Estado') ?? '').toLowerCase();
    const lista = Array.isArray(de(x, 'cruces', 'Cruces')) ? de(x, 'cruces', 'Cruces') : [];
    return {
      // UNA-CIFRA-DANOS-CONSUMIDORES-T1 (nexo-api 749): el dano se ve en su DIA OPERATIVO (el conteo de la manana es del dia
      // anterior), el mismo con que la API lo filtra; fechaOperativa (registro) solo si la API no manda diaOperativo.
      fecha: String(de(x, 'diaOperativo', 'dia_operativo', 'DiaOperativo') ?? de(x, 'fechaOperativa', 'fecha_operativa', 'FechaOperativa') ?? '').slice(0, 10),
      insumo: String(de(x, 'insumo', 'Insumo') ?? ''),
      unidad: String(de(x, 'unidad', 'Unidad') ?? ''),
      colaborador: String(de(x, 'colaborador', 'Colaborador') ?? ''),
      unidadesOriginal: num(de(x, 'unidadesOriginal', 'unidades_original')),
      unidadesVigentes: num(de(x, 'unidadesVigentes', 'unidades_vigentes')),
      valorOriginal: num(de(x, 'valorOriginal', 'valor_original')),
      valorVigente: num(de(x, 'valorVigente', 'valor_vigente')),
      estado: ESTADOS.has(estado) ? estado : 'vigente',
      cruces: lista.length,
      // PANEL-CRUCES-POR-SOLICITUD-T1 (contrato A #31980): cada compensacion con su canal y la solicitud que la aprobo.
      compensaciones: lista.map((c) => ({
        canal: String(de(c, 'canal', 'Canal') ?? ''),
        solicitud: String(de(c, 'solicitudRef', 'solicitud_ref', 'SolicitudRef') ?? '').trim(),
        unidades: num(de(c, 'unidadesAutorizadas', 'unidades_autorizadas')),
        autorizadoPor: String(de(c, 'autorizadoPor', 'autorizado_por') ?? ''),
        autorizadoAt: String(de(c, 'autorizadoAt', 'autorizado_at') ?? '')
      }))
    };
  }).sort((a, b) => a.fecha.localeCompare(b.fecha) || a.insumo.localeCompare(b.insumo));
  const tv = de(d, 'totalVigente', 'total_vigente');
  const to = de(d, 'totalOriginal', 'total_original');
  return { items, totalVigente: tv === undefined ? null : num(tv), totalOriginal: to === undefined ? null : num(to) };
}

/**
 * Como se pinta una fila (D-138 ampliacion 3): compensado = no se lista (oculto); parcial y vigente = solo la cifra
 * vigente, sin tachado ni etiqueta.
 */
export function filaDeDano(it) {
  // Nota de Q #30146 y Ares #30150: un dano SIN COSTO (unidades pero valor 0: el insumo no tiene costo cargado) no puede
  // verse como "$ 0", porque un cero se lee como buena noticia. Dice "sin costo".
  const sinCosto = it.valorOriginal === 0 && it.unidadesOriginal > 0;
  return { sinCosto, oculto: it.estado === 'compensado', valor: it.valorVigente };
}

/** Los danos que ve el usuario: todos menos los compensados del todo. */
export function danosVisibles(items) {
  return (items || []).filter((it) => !filaDeDano(it).oculto);
}

/**
 * PANEL-CRUCES-POR-SOLICITUD-T1: lo que se aplico por una solicitud aprobada (Argemiro O, D-145/D-146), agrupado por
 * solicitud. Incluye los danos compensados del todo, que la lista de arriba no muestra: aqui se ve que se ajusto y por
 * que. Solo cuentan las compensaciones con solicitud; las de chat y panel no traen y no aparecen. Sin solicitudes = [].
 * Por fila: unidades ajustadas por esa solicitud y lo que queda vigente del dano (el residuo, del servidor).
 */
export function aplicadoPorSolicitud(items) {
  const grupos = new Map();
  for (const it of items || []) {
    for (const c of it.compensaciones || []) {
      if (!c.solicitud) continue;
      if (!grupos.has(c.solicitud)) grupos.set(c.solicitud, { solicitud: c.solicitud, autorizadoPor: new Set(), unidades: 0, filas: [] });
      const g = grupos.get(c.solicitud);
      if (c.autorizadoPor) g.autorizadoPor.add(c.autorizadoPor);
      g.unidades += c.unidades;
      g.filas.push({ fecha: it.fecha, insumo: it.insumo, unidad: it.unidad, unidades: c.unidades, queda: it.unidadesVigentes });
    }
  }
  return [...grupos.values()]
    .map((g) => ({ ...g, autorizadoPor: [...g.autorizadoPor].join(', ') }))
    .sort((a, b) => a.solicitud.localeCompare(b.solicitud, 'es', { numeric: true }));
}

/** Cuantos danos visibles no tienen costo: no suman al total (su valor es 0) y la pantalla lo dice. */
export function cuantosSinCosto(items) {
  return danosVisibles(items).filter((it) => filaDeDano(it).sinCosto).length;
}
